import { Link } from "react-router-dom";
import { ArrowRight, Target, Eye, Building2 } from "lucide-react";

const ABOUT_IMG = "https://images.unsplash.com/photo-1764690690771-b4522d66b433?auto=format&fit=crop&w=1200&q=80";

const partnersImage = "https://customer-assets.emergentagent.com/job_1dfad621-fc79-4cd5-b25a-5a11bf07839e/artifacts/vjgi0yq7_ChatGPT%20Image%20Feb%2022%2C%202026%2C%2002_26_45%20PM.png";

export default function About() {
  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 bg-[#0F172A]" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#00B4D8] text-sm font-bold uppercase tracking-wider">
              About Us
            </span>
            <h1 className="font-heading text-5xl md:text-6xl font-black tracking-tight text-white mt-4 mb-6">
              Building America's Telecommunications Infrastructure
            </h1>
            <p className="text-xl text-slate-400 leading-relaxed">
              C&F Communications was built to support the growing demand for high-quality 
              telecommunications infrastructure across the United States.
            </p>
          </div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-20 md:py-32 bg-white" data-testid="company-overview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span className="text-[#00B4D8] text-sm font-bold uppercase tracking-wider">
                Company Overview
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mt-4 mb-6">
                Who We Are
              </h2>
              <div className="section-line mb-8"></div>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                C&F Communications was built to support the growing demand for high-quality 
                telecommunications infrastructure across the United States.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                Our team delivers fiber and broadband network construction services with a 
                focus on reliability, execution speed, and compliance with carrier standards.
              </p>
              <div className="bg-slate-50 p-6 border-l-4 border-[#00B4D8]">
                <p className="text-slate-700 font-medium">
                  We work with major telecommunications operators and broadband providers to 
                  deliver infrastructure that connects communities across America.
                </p>
              </div>
            </div>
            <div className="relative bg-[#0F172A] p-8 flex items-center justify-center">
              <div className="text-center">
                <div className="text-5xl font-black text-[#00B4D8] mb-2">Est. 2022</div>
                <div className="text-sm uppercase tracking-wider text-slate-400">Founded February</div>
                <div className="mt-6 pt-6 border-t border-slate-700">
                  <div className="text-3xl font-black text-white mb-1">Ohio</div>
                  <div className="text-xs uppercase tracking-wider text-slate-500">Headquarters</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-20 md:py-32 bg-slate-50 grid-pattern" data-testid="partners-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#00B4D8] text-sm font-bold uppercase tracking-wider">
              Our Partners
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mt-4 mb-6">
              Trusted By Industry Leaders
            </h2>
            <div className="section-line mx-auto mb-6"></div>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              We work with major telecommunications operators and broadband providers including:
            </p>
          </div>

          <div className="flex justify-center">
            <img 
              src={partnersImage} 
              alt="Our Partners: AT&T, Spectrum, Frontier Communications, Brightspeed"
              className="max-w-3xl w-full h-auto object-contain"
              data-testid="partners-logos"
            />
          </div>
          <p className="text-center text-slate-500 mt-8 text-sm">
            And other regional & national carriers
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 md:py-32 bg-white" data-testid="mission-vision">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Mission */}
            <div className="bg-[#0F172A] p-10 md:p-12" data-testid="mission-card">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-[#00B4D8] flex items-center justify-center">
                  <Target className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-heading text-2xl font-bold text-white">Our Mission</h3>
              </div>
              <p className="text-lg text-slate-300 leading-relaxed">
                To build reliable telecommunications infrastructure that connects communities 
                and supports the future of broadband expansion across America.
              </p>
            </div>

            {/* Vision */}
            <div className="bg-slate-50 p-10 md:p-12 border-l-4 border-[#00B4D8]" data-testid="vision-card">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-[#00B4D8] flex items-center justify-center">
                  <Eye className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-heading text-2xl font-bold text-slate-900">Our Vision</h3>
              </div>
              <p className="text-lg text-slate-600 leading-relaxed">
                To become a trusted national partner for fiber and HFC network construction 
                by delivering consistent quality, safe operations, and scalable field performance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#00B4D8]" data-testid="about-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Work With Us?
          </h2>
          <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
            Let's discuss how C&F Communications can support your next project.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center bg-white text-[#0F172A] px-8 py-4 text-sm font-bold uppercase tracking-wider transition-all duration-300 shadow-[4px_4px_0px_0px_rgba(15,23,42,0.3)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(15,23,42,0.3)]"
            data-testid="about-cta-button"
          >
            Contact Us Today
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
